const toTimestamp = (value) => {
  if (value == null) return Number.POSITIVE_INFINITY;

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  }

  if (typeof value === "string") {
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  }

  if (typeof value?.toDate === "function") {
    const timestamp = value.toDate()?.getTime?.();
    return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
  }

  return Number.POSITIVE_INFINITY;
};

const isFiniteTimestamp = (value) => Number.isFinite(value);

const normalizeCap = (cap) => {
  const parsedCap = Number(cap);
  if (!Number.isFinite(parsedCap)) return 1;
  return Math.max(1, Math.floor(parsedCap));
};

export const selectRetainedBlocks = ({
  blocks,
  activeBlockId = null,
  now = new Date(),
  maxFutureDatedBlocks = 3,
  maxRecentUndatedBlocks = 4,
  undatedWindowDays = 21,
  maxRecentLastUpdatedBlocks = 3,
  recentLastUpdateWindowDays = 14,
} = {}) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return [];

  const normalizedFutureCap = normalizeCap(maxFutureDatedBlocks);
  const normalizedUndatedCap = normalizeCap(maxRecentUndatedBlocks);
  const normalizedUndatedWindowDays = normalizeCap(undatedWindowDays);
  const normalizedRecentLastUpdatedCap = normalizeCap(maxRecentLastUpdatedBlocks);
  const normalizedRecentLastUpdateWindowDays = normalizeCap(
    recentLastUpdateWindowDays
  );
  const nowTimestamp = toTimestamp(now);
  const undatedWindowStartTimestamp =
    nowTimestamp - normalizedUndatedWindowDays * 24 * 60 * 60 * 1000;
  const recentLastUpdateWindowStartTimestamp =
    nowTimestamp - normalizedRecentLastUpdateWindowDays * 24 * 60 * 60 * 1000;

  const normalizedActiveId =
    activeBlockId == null ? null : String(activeBlockId).toLowerCase();

  const entries = blocks.map((block) => {
    const id = String(block?.id ?? "");
    const initialDateTimestamp = toTimestamp(block?.initialDate);
    const lastUpdateTimestamp = toTimestamp(
      block?.last_update ?? block?.lastUpdate
    );

    return {
      block,
      id,
      normalizedId: id.toLowerCase(),
      label: String(block?.label ?? ""),
      initialDateTimestamp,
      lastUpdateTimestamp,
      isDated: isFiniteTimestamp(initialDateTimestamp),
      hasValidLastUpdate: isFiniteTimestamp(lastUpdateTimestamp),
    };
  });

  const activeEntry =
    normalizedActiveId == null
      ? null
      : entries.find((entry) => entry.normalizedId === normalizedActiveId) ??
        null;
  const activeDateTimestamp = activeEntry?.initialDateTimestamp;
  const activeLabel = activeEntry?.label ?? "";

  const futureDatedEntries = entries
    .filter(
      (entry) =>
        entry.normalizedId !== activeEntry?.normalizedId &&
        entry.isDated &&
        isFiniteTimestamp(activeDateTimestamp) &&
        entry.initialDateTimestamp > activeDateTimestamp
    )
    .sort((a, b) => {
      if (a.initialDateTimestamp !== b.initialDateTimestamp) {
        return a.initialDateTimestamp - b.initialDateTimestamp;
      }
      return a.id.localeCompare(b.id);
    })
    .slice(0, normalizedFutureCap);

  const recentUndatedEntries = entries
    .filter(
      (entry) =>
        entry.normalizedId !== activeEntry?.normalizedId &&
        !entry.isDated &&
        entry.hasValidLastUpdate &&
        entry.lastUpdateTimestamp >= undatedWindowStartTimestamp
    )
    .sort((a, b) => {
      if (a.lastUpdateTimestamp !== b.lastUpdateTimestamp) {
        return b.lastUpdateTimestamp - a.lastUpdateTimestamp;
      }

      const aSharesLabel = a.label === activeLabel ? 1 : 0;
      const bSharesLabel = b.label === activeLabel ? 1 : 0;
      if (aSharesLabel !== bSharesLabel) {
        return bSharesLabel - aSharesLabel;
      }

      return a.id.localeCompare(b.id);
    })
    .slice(0, normalizedUndatedCap);

  const retainedNormalizedIds = new Set([
    ...(activeEntry?.normalizedId ? [activeEntry.normalizedId] : []),
    ...futureDatedEntries.map((entry) => entry.normalizedId),
    ...recentUndatedEntries.map((entry) => entry.normalizedId),
  ]);

  const recentLastUpdatedEntries = entries
    .filter(
      (entry) =>
        !retainedNormalizedIds.has(entry.normalizedId) &&
        entry.hasValidLastUpdate &&
        entry.lastUpdateTimestamp >= recentLastUpdateWindowStartTimestamp
    )
    .sort((a, b) => {
      if (a.lastUpdateTimestamp !== b.lastUpdateTimestamp) {
        return b.lastUpdateTimestamp - a.lastUpdateTimestamp;
      }
      return a.id.localeCompare(b.id);
    })
    .slice(0, normalizedRecentLastUpdatedCap);

  const retainedBlocks = [
    ...(activeEntry ? [activeEntry.block] : []),
    ...futureDatedEntries.map((entry) => entry.block),
    ...recentUndatedEntries.map((entry) => entry.block),
    ...recentLastUpdatedEntries.map((entry) => entry.block),
  ];

  const seenIds = new Set();
  return retainedBlocks.filter((block) => {
    const id = String(block?.id ?? "");
    if (!id || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
};

export const shouldRehydrateFullFetch = ({
  isFetched = false,
  wasPartiallyEvicted = false,
  isOffline = false,
} = {}) => {
  if (isOffline) return false;
  if (wasPartiallyEvicted) return true;
  return !isFetched;
};
